import * as React from 'react';
import { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { doc, getDoc } from '@firebase/firestore';
import CloseIcon from '@mui/icons-material/Close';
import LogoutIcon from '@mui/icons-material/Logout';
import MenuIcon from '@mui/icons-material/Menu';
import AppBar from '@mui/material/AppBar';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import { signOut } from 'firebase/auth';
import { useTranslation } from 'react-i18next';

import { getLogo } from '../../api/aws/s3';
import { auth } from '../../api/firebase/config';
import { companiesRef } from '../../api/firebase/firestore/company';
import { usersRef } from '../../api/firebase/firestore/user';
import logo from '../../components/assets/logo.svg';
import { black, changeHeaderColorByEnv } from '../../constants/Colors';
import { screenType } from '../../constants/enum';
import { AuthContext } from '../../contexts/auth';
import { throwError } from '../../modules/error';

import SidebarLinks from './SidebarLinks';

import type { RegisterScreenProps } from '../../api/firebase/auth/authType';

type SidebarInfoProps = RegisterScreenProps & {
  screenType: number;
};

const styles = {
  avatar: {
    height: '40px',
    width: '40px'
  },
  bottomContent: {
    '@media screen and (max-height:800px)': {
      position: 'static'
    },
    '@media screen and (max-width:960px) and (max-height:800px)': {
      position: 'absolute'
    },
    // eslint-disable-next-line
    '@media screen and (max-width:960px) and (max-height:726px)': {
      position: 'static'
    },
    bottom: '0',
    position: 'absolute',
    width: '100%'
  },
  bottomContentInfo: {
    alignItems: 'left',
    display: 'flex',
    flexDirection: 'column',
    margin: '12px 16px 0'
  },
  closeIcon: {
    ':hover': {
      cursor: 'pointer'
    },
    color: '#FFF',
    height: '24px',
    margin: '12px 0 0 12px',
    width: '24px'
  },
  companyType: {
    color: '#FFF',
    fontSize: '12px'
  },
  divider: {
    borderColor: '#494949',
    height: '1px'
  },
  drawer: {
    '.MuiDrawer-paper': {
      bgcolor: black,
      width: '304px'
    }
  },
  header: {
    bgcolor: changeHeaderColorByEnv(),
    boxShadow: 'none',
    height: '48px'
  },
  headerInner: {
    '&.MuiToolbar-root': {
      minHeight: '48px'
    }
  },
  headerTitle: {
    flexGrow: 1,
    fontFamily: 'Noto Sans JP, sans-serif',
    fontSize: '16px',
    fontWeight: 'bold'
  },
  icon: {
    color: '#FFF',
    width: '24px'
  },
  infoText: {
    '@media screen and (max-width:960px)': {
      fontSize: '18px'
    },
    WebkitBoxOrient: 'vertical',
    WebkitLineClamp: '2',
    color: '#FFF',
    display: '-webkit-box',
    fontSize: '20px',
    marginLeft: '8px',
    maxWidth: '176px',
    overflow: 'hidden'
  },
  logo: {
    '@media screen and (max-width:960px)': {
      height: '28px',
      width: '105px'
    },
    borderRadius: 0,
    height: '40px',
    width: '150px'
  },
  logoutButton: {
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.24)',
      borderRadius: '4px'
    },
    height: '40px',
    margin: '16px 8px',
    padding: '8px 0'
  },
  logoutButtonText: {
    '.MuiTypography-root': {
      fontSize: '14px'
    },
    color: '#FFF'
  },
  sidebar: {
    '@media screen and (max-height:800px)': {
      overflowY: 'scroll'
    },
    '@media screen and (max-width:960px)': {
      display: 'none'
    },
    bgcolor: changeHeaderColorByEnv(),
    height: '100vh',
    minWidth: '256px',
    position: 'sticky',
    top: 0
  },
  spHeader: {
    '@media screen and (max-width:960px)': {
      display: 'block'
    },
    display: 'none',
    flexGrow: 1,
    position: 'sticky',
    top: 0,
    zIndex: 10
  },
  spMenu: {
    position: 'static',
    width: '304px'
  },
  title: {
    color: '#FFF',
    fontSize: '24px',
    marginBottom: '40px',
    paddingLeft: '16px',
    paddingTop: '40px'
  },
  userInfo: {
    alignItems: 'center',
    display: 'flex',
    flexDirection: 'row',
    margin: '12px 0 24px'
  }
};

const Sidebar: React.FC<SidebarInfoProps> = (props) => {
  const { t } = useTranslation();

  const [companyName, setCompanyName] = useState<string>('');

  const [companyType, setCompanyType] = useState<string>('');

  const [s3ImageData, setS3ImageData] = useState<string>('');

  const [drawerOpen, setDrawerOpen] = useState(false);

  const navigate = useNavigate();

  const handleMenuToggle = (): void => {
    setDrawerOpen(!drawerOpen);
  };

  const { currentUser } = useContext(AuthContext);

  const init = async (): Promise<void> => {
    try {
      if (currentUser) {
        const userRef = doc(usersRef, currentUser.uid);
        const userSnap = await getDoc(userRef);
        if (userSnap.exists()) {
          const ownCampanyRef = doc(
            companiesRef,
            userSnap.data()?.companyId as string
          );
          const companySnap = await getDoc(ownCampanyRef);
          setCompanyName(companySnap.data()?.name as string);
          const image = await getLogo(companySnap);
          setS3ImageData(image);

          if (companySnap.data()?.type === screenType.client) {
            setCompanyType(t('type.client'));
          } else if (companySnap.data()?.type === screenType.agent) {
            setCompanyType(t('type.agent'));
          } else if (companySnap.data()?.type === screenType.publisher) {
            setCompanyType(t('type.publisher'));
          }
        }
      }
    } catch (e) {
      throwError(e);
    }
  };

  const [isMounted, setIsMounted] = useState(true);

  useEffect(() => {
    setIsMounted(true);

    void (async () => {
      await init();
    })();

    return () => {
      setIsMounted(false);
    };
  }, []);

  if (!isMounted) {
    return null;
  }

  return (
    <>
      <>
        <Box sx={styles.sidebar}>
          <Avatar alt="logo" src={logo} sx={[styles.logo, styles.title]} />
          <SidebarLinks screenType={props.screenType} />
          <Typography component="div" sx={styles.bottomContent}>
            <ListItemButton sx={styles.logoutButton}>
              <Button
                onClick={() => {
                  void (async () => {
                    await signOut(auth)
                      .then(() => {
                        navigate('/auth/login');
                      })
                      .catch((error) => {
                        throwError(error);
                      });
                  })();
                }}
              >
                <ListItemIcon sx={{ minWidth: '48px' }}>
                  <LogoutIcon sx={styles.icon} />
                </ListItemIcon>
                <ListItemText
                  primary={t('sidebar.logout')}
                  sx={styles.logoutButtonText}
                />
              </Button>
            </ListItemButton>
            <Divider sx={styles.divider} />
            <Typography component="div" sx={styles.bottomContentInfo}>
              <Typography component="p" sx={styles.companyType}>
                {companyType}
              </Typography>
              <Typography component="div" sx={styles.userInfo}>
                <Avatar sx={styles.avatar} src={s3ImageData} />
                <Typography component="p" sx={styles.infoText}>
                  {companyName}
                </Typography>
              </Typography>
            </Typography>
          </Typography>
        </Box>
      </>
      <>
        <Box sx={styles.spHeader}>
          <AppBar position="static" sx={styles.header}>
            <Toolbar sx={styles.headerInner}>
              <IconButton
                size="large"
                edge="start"
                color="inherit"
                aria-label="menu"
                sx={{ mr: 2 }}
                onClick={handleMenuToggle}
              >
                <MenuIcon />
              </IconButton>
              <Avatar alt="logo" src={logo} sx={styles.logo} />
            </Toolbar>
          </AppBar>
        </Box>
      </>
      <>
        <Drawer open={drawerOpen} onClose={handleMenuToggle} sx={styles.drawer}>
          <Box sx={styles.spMenu}>
            <CloseIcon sx={styles.closeIcon} onClick={handleMenuToggle} />
            <SidebarLinks screenType={props.screenType} />
            <Typography component="div" sx={styles.bottomContent}>
              <ListItemButton sx={styles.logoutButton}>
                <Button
                  onClick={() => {
                    void (async () => {
                      await signOut(auth)
                        .then(() => {
                          navigate('/auth/login');
                        })
                        .catch((error) => {
                          throwError(error);
                        });
                    })();
                  }}
                >
                  <ListItemIcon sx={{ minWidth: '48px' }}>
                    <LogoutIcon sx={styles.icon} />
                  </ListItemIcon>
                  <ListItemText
                    primary={t('sidebar.logout')}
                    sx={styles.logoutButtonText}
                  />
                </Button>
              </ListItemButton>
              <Divider sx={styles.divider} />
              <Typography component="div" sx={styles.bottomContentInfo}>
                <Typography component="p" sx={styles.companyType}>
                  {companyType}
                </Typography>
                <Typography component="div" sx={styles.userInfo}>
                  <Avatar sx={styles.avatar} src={s3ImageData} />
                  <Typography component="p" sx={styles.infoText}>
                    {companyName}
                  </Typography>
                </Typography>
              </Typography>
            </Typography>
          </Box>
        </Drawer>
      </>
    </>
  );
};

export default Sidebar;
